'use client'
import * as React from 'react'
import PropTypes from 'prop-types'

// --- Hooks ---
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'

// --- Icons ---
import { ApplyLinkIcon } from '@tiptap-ui/components/tiptap-icons/apply-link-icon'
import { ExternalLinkIcon } from '@tiptap-ui/components/tiptap-icons/external-link-icon'
import { LinkIcon } from '@tiptap-ui/components/tiptap-icons/link-icon'
import { TrashIcon } from '@tiptap-ui/components/tiptap-icons/trash-icon'

import { useLinkPopover } from '@tiptap-ui/components/tiptap-ui/link-popover'

import { Button, ButtonGroup } from '@tiptap-ui/components/tiptap-ui-primitive/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@tiptap-ui/components/tiptap-ui-primitive/popover'
import {
  Card,
  CardBody,
  CardItemGroup,
} from '@tiptap-ui/components/tiptap-ui-primitive/card'
import { Input, InputGroup } from '@tiptap-ui/components/tiptap-ui-primitive/input'

/**
 * Link button component for triggering the link popover
 */
export const LinkButton = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <Button
      type='button'
      className={className}
      data-style='ghost'
      role='button'
      tabIndex={-1}
      aria-label='Link'
      tooltip='Link'
      ref={ref}
      {...props}
    >
      {children || <LinkIcon className='tiptap-button-icon' />}
    </Button>
  )
})

LinkButton.displayName = 'LinkButton'
LinkButton.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
}

/**
 * Main content component for the link popover
 */
const LinkMain = ({
  url,
  setUrl,
  setLink,
  removeLink,
  openLink,
  isActive,
}) => {
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setLink()
    }
  }

  return (
    <Card>
      <CardBody>
        <CardItemGroup
          orientation='horizontal'
          style={{ padding: '4px' }}
        >
          <InputGroup>
            <Input
              type='url'
              placeholder='Paste a link...'
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              autoComplete='off'
              autoCorrect='off'
              autoCapitalize='off'
            />
          </InputGroup>

          <ButtonGroup orientation='horizontal'>
            <Button
              type='button'
              onClick={setLink}
              title='Apply link'
              disabled={!url && !isActive}
              data-style='ghost'
            >
              <ApplyLinkIcon className='tiptap-button-icon' />
            </Button>
            <Button
              type='button'
              onClick={openLink}
              title='Open in new window'
              disabled={!url && !isActive}
              data-style='ghost'
            >
              <ExternalLinkIcon className='tiptap-button-icon' />
            </Button>

            <Button
              type='button'
              onClick={removeLink}
              title='Remove link'
              disabled={!url && !isActive}
              data-style='ghost'
            >
              <TrashIcon className='tiptap-button-icon' />
            </Button>
          </ButtonGroup>
        </CardItemGroup>
      </CardBody>
    </Card>
  )
}

LinkMain.propTypes = {
  url: PropTypes.string,
  setUrl: PropTypes.func.isRequired,
  setLink: PropTypes.func.isRequired,
  removeLink: PropTypes.func.isRequired,
  openLink: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
}

/**
 * Link content component for standalone use
 */
export const LinkContent = ({ editor }) => {
  const linkPopover = useLinkPopover({
    editor,
  })

  return <LinkMain {...linkPopover} />
}

LinkContent.propTypes = {
  editor: PropTypes.object,
}

/**
 * Link popover component for Tiptap editors.
 *
 * For custom popover implementations, use the `useLinkPopover` hook instead.
 */
export const LinkPopover = React.forwardRef((
  {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onSetLink,
    onOpenChange,
    autoOpenOnLinkActive = true,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const [isOpen, setIsOpen] = React.useState(false)

  const {
    isVisible,
    canSet,
    isActive,
    url,
    setUrl,
    setLink,
    removeLink,
    openLink,
    label,
    Icon,
  } = useLinkPopover({
    editor,
    hideWhenUnavailable,
    onSetLink,
  })

  const handleOnOpenChange = React.useCallback((nextIsOpen) => {
    setIsOpen(nextIsOpen)
    onOpenChange?.(nextIsOpen)
  }, [onOpenChange])

  const handleSetLink = React.useCallback(() => {
    setLink()
    setIsOpen(false)
  }, [setLink])

  const handleClick = React.useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    setIsOpen(!isOpen)
  }, [onClick, isOpen])

  React.useEffect(() => {
    if (autoOpenOnLinkActive && isActive) {
      setIsOpen(true)
    }
  }, [autoOpenOnLinkActive, isActive])

  if (!isVisible) {
    return null
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOnOpenChange}>
      <PopoverTrigger asChild>
        <LinkButton
          disabled={!canSet}
          data-active-state={isActive ? 'on' : 'off'}
          data-disabled={!canSet}
          aria-label={label}
          aria-pressed={isActive}
          onClick={handleClick}
          {...buttonProps}
          ref={ref}
        >
          {children ?? <Icon className='tiptap-button-icon' />}
        </LinkButton>
      </PopoverTrigger>
      <PopoverContent>
        <LinkMain
          url={url}
          setUrl={setUrl}
          setLink={handleSetLink}
          removeLink={removeLink}
          openLink={openLink}
          isActive={isActive}
        />
      </PopoverContent>
    </Popover>
  )
})

LinkPopover.displayName = 'LinkPopover'

LinkPopover.propTypes = {
  editor: PropTypes.object,
  hideWhenUnavailable: PropTypes.bool,
  onSetLink: PropTypes.func,
  onOpenChange: PropTypes.func,
  autoOpenOnLinkActive: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
}

export default LinkPopover
